import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper,
  Stack,
  useTheme
} from '@mui/material';
import { 
  RateReview as RateReviewIcon,
  Star as StarIcon,
  ChatBubbleOutline as ChatBubbleIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

const NoReviewsYet = ({ businessName, onWriteReview, isAuthenticated }) => {
  const theme = useTheme();

  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: 4, 
        border: 'none',
        background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${theme.palette.primary.light}20, ${theme.palette.secondary.light}20)`,
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -30,
          left: -30,
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${theme.palette.secondary.light}15, ${theme.palette.primary.light}15)`,
          zIndex: 0
        }}
      />

      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Icon section */}
        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)'
            }}
          >
            <StarIcon sx={{ color: 'white', fontSize: 30 }} />
          </Box>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 15px rgba(156, 39, 176, 0.3)',
              mt: '10px !important'
            }}
          >
            <ChatBubbleIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Box
            sx={{
              width: 45,
              height: 45,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
              mt: '15px !important'
            }}
          >
            <TrendingUpIcon sx={{ color: 'white', fontSize: 20 }} />
          </Box>
        </Stack>

        {/* Main message */}
        <Typography 
          variant="h5" 
          component="h3"
          sx={{ 
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 2
          }}
        >
          No Reviews Yet
        </Typography>

        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ 
            mb: 1,
            lineHeight: 1.6,
            maxWidth: 400,
            mx: 'auto'
          }}
        >
          <strong>{businessName}</strong> is waiting for its first review! 
        </Typography>

        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            mb: 4,
            lineHeight: 1.6,
            maxWidth: 400,
            mx: 'auto'
          }}
        >
          Be the first to share your experience and help others in the community discover this business.
        </Typography>

        {/* Call to action */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<RateReviewIcon />}
            onClick={onWriteReview}
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                boxShadow: '0 6px 25px rgba(25, 118, 210, 0.4)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Write the First Review
          </Button>

          {!isAuthenticated && (
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ 
                fontSize: 12,
                fontStyle: 'italic'
              }}
            >
              Sign in required to write reviews
            </Typography>
          )}
        </Box>

        {/* Benefits section */}
        <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography 
            variant="subtitle2" 
            color="text.secondary"
            sx={{ mb: 2, fontWeight: 500 }}
          >
            Why write the first review?
          </Typography>
          
          <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap">
            <Box sx={{ textAlign: 'center', minWidth: 120 }}>
              <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600, display: 'block' }}>
                🎯 Be Helpful
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Guide future customers
              </Typography>
            </Box>
            
            <Box sx={{ textAlign: 'center', minWidth: 120 }}>
              <Typography variant="caption" color="secondary.main" sx={{ fontWeight: 600, display: 'block' }}>
                🌟 Build Trust
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Share authentic experiences
              </Typography>
            </Box>
            
            <Box sx={{ textAlign: 'center', minWidth: 120 }}>
              <Typography variant="caption" color="success.main" sx={{ fontWeight: 600, display: 'block' }}>
                🚀 Lead the Way
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Be a community leader
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
};

export default NoReviewsYet;